import { api } from './apiService';
import axiosInstance from './axiosInstance';

export type ReferenceType = 'notebook' | 'video';

export type VideoPhase = 'idle' | 'loading' | 'segmenting' | 'ready' | 'failed';

export type CampaignPost = {
  id: string;
  platform: string;
  channel?: string;
  body: string;
  hashtags?: string;
  recipient?: string;
  readyToPublish: boolean;
  scheduledAt?: string;
  status?: 'draft' | 'scheduled' | 'published' | string;
  deskPostId?: string;
  externalId?: string;
  deliveryError?: string;
  updatedAt?: string;
};

export type SsembleClip = {
  title: string;
  video_url: string;
  viral_score: number;
};

export type VideoJob = {
  phase: VideoPhase;
  sourceUrl?: string;
  sourceKind?: string;
  /** Public URL for an uploaded file (Ssemble fetches this). */
  publicVideoUrl?: string;
  contentType?: string;
  ssembleRequestId?: string;
  ssembleStatus?: string;
  ssembleProgress?: number;
  clips?: SsembleClip[];
  errorMessage?: string;
};

export type SocialCampaign = {
  id: string;
  tenantId?: string;
  title: string;
  referenceType: ReferenceType;
  referenceId: string;
  referenceLabel?: string;
  posts: CampaignPost[];
  video?: VideoJob;
  createdAt?: string;
  updatedAt?: string;
};

export async function listCampaigns(): Promise<SocialCampaign[]> {
  const res = (await api.get('/api/social-studio/campaigns')) as { campaigns?: SocialCampaign[] };
  return res.campaigns ?? [];
}

export async function createCampaign(body: {
  referenceType: ReferenceType;
  referenceId?: string;
  referenceLabel?: string;
  title?: string;
}): Promise<SocialCampaign> {
  return (await api.post('/api/social-studio/campaigns', body)) as SocialCampaign;
}

export async function getCampaign(id: string): Promise<SocialCampaign> {
  return (await api.get(`/api/social-studio/campaigns/${id}`)) as SocialCampaign;
}

export async function updateCampaign(
  id: string,
  body: { title?: string; posts?: CampaignPost[] }
): Promise<SocialCampaign> {
  return (await api.patch(`/api/social-studio/campaigns/${id}`, body)) as SocialCampaign;
}

export async function addCampaignPost(
  id: string,
  body: { platform?: string; channel?: string; body: string; hashtags?: string; recipient?: string }
): Promise<SocialCampaign> {
  return (await api.post(`/api/social-studio/campaigns/${id}/posts`, body)) as SocialCampaign;
}

export async function scheduleCampaignPost(
  id: string,
  postId: string,
  body: { scheduledAt: string; recipient?: string; body?: string; hashtags?: string; updateCopy?: boolean }
): Promise<SocialCampaign> {
  return (await api.post(`/api/social-studio/campaigns/${id}/posts/${postId}/schedule`, body)) as SocialCampaign;
}

export async function deliverCampaignPost(
  id: string,
  postId: string,
  body?: { recipient?: string; body?: string; hashtags?: string; updateCopy?: boolean }
): Promise<SocialCampaign> {
  return (await api.post(`/api/social-studio/campaigns/${id}/posts/${postId}/deliver`, body ?? {})) as SocialCampaign;
}

export async function generatePosts(id: string): Promise<SocialCampaign> {
  return (await api.post(`/api/social-studio/campaigns/${id}/generate-posts`, {})) as SocialCampaign;
}

export async function startVideoSegmentation(
  id: string,
  opts?: { videoUrl?: string; sourceKind?: 'youtube' | 'url' | 'upload' }
): Promise<SocialCampaign> {
  const body: Record<string, string> = {};
  if (opts?.videoUrl != null) body.videoUrl = opts.videoUrl;
  if (opts?.sourceKind) body.sourceKind = opts.sourceKind;
  return (await api.post(`/api/social-studio/campaigns/${id}/video/start`, body)) as SocialCampaign;
}

/** Multipart upload; server stores the file and returns a public HTTPS URL for Ssemble. */
export async function uploadCampaignVideo(
  campaignId: string,
  file: File,
  onProgress?: (pct: number) => void
): Promise<SocialCampaign> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axiosInstance.post(`/api/social-studio/campaigns/${campaignId}/video/upload`, fd, {
    timeout: 30 * 60 * 1000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((100 * e.loaded) / e.total));
    },
  });
  return res.data as SocialCampaign;
}
